// AdminPanel/RoutingTab — admin-scoped view of ONE tenant's operator routing.
//
// The standalone ManageOperators → "Tenant Routing" tab does the same CRUD
// across ALL tenants (list + search + bulk assign). This admin variant is
// scoped to the single tenant whose detail modal is open, so a support agent
// can set that tenant's per-message-type SMSC route inline without leaving
// the admin panel.
//
// Reuses the OperatorManagerService actions (control_plane/operator_manager
// .proto) — same endpoints the all-tenants view uses:
//   - listTenantRoutes(search)          GET  .../tenant-routes?search=<orgId>
//                                        (searched by orgId → the one tenant)
//   - listOperators()                   GET  .../operators   (route target list)
//   - setOperatorRoute({...})           POST .../routes      (assign / reassign)
//   - removeOperatorRoute(routeId)      DELETE .../routes/{id}
//
// Routing is per CONCRETE message type — OTP / Transactional / Promotional each
// have their own primary (priority 1) + optional standbys. Reassign = set the
// new operator at the same type+priority, then delete the old route (2-leg,
// mirrors tenantRouting.jsx so a reassign never leaves a gap).
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Add, Close, SwapHoriz, Refresh, Route as RouteIcon} from '@mui/icons-material';
import {
  Box, Button, Card, CardContent, Typography, Chip, CircularProgress, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Alert, Snackbar, Tabs, Tab,
} from '@mui/material';

import DltTmdTab from './DltTmdTab';

import {
  listOperators,
  listTenantRoutes,
  setOperatorRoute,
  removeOperatorRoute,
} from '../../actions/manageOperators';

// The concrete message types that can be routed, in display order. Keys match
// the backend `message_type` values; there is no 'any' — every type routes
// independently.
const MESSAGE_TYPES = [
  {key: 'otp', label: 'OTP'},
  {key: 'transactional', label: 'Transactional'},
  {key: 'promotional', label: 'Promotional'},
];

// Normalize a route object — the API returns either camelCase or snake_case
// depending on the gateway transcoding path, so read both.
function normRoute(r) {
  return {
    routeId: r.routeId || r.route_id,
    smscId: r.smscId || r.smsc_id || '',
    operatorName: r.operatorName || r.operator_name || '',
    messageType: (r.messageType || r.message_type || 'transactional').toLowerCase(),
    priority: r.priority ?? 0,
    isPrimary: r.isPrimary ?? r.is_primary ?? false,
  };
}

class AdminRoutingTab extends Component {
  state = {
    subTab: 0,            // 0 = Operator Routing, 1 = DLT TMD (read-only preview)
    routes: [],           // normalized routes for THIS tenant
    operators: [],        // SMSC operators (assign targets)
    loading: false,
    assignDialog: null,   // { messageType, operatorId, priority, editRouteId }
    busy: false,          // a set/remove mutation is in flight
    snackbar: {open: false, message: '', severity: 'success'},
  };

  componentDidMount() {
    this.fetchAll();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tenantId !== this.props.tenantId) {
      this.fetchAll();
      return;
    }
    // Fire-and-forget thunks land their payload in the store; watch for a
    // reference change on the matching response while our request flag is set
    // (the flag stops a stale response from a prior mount re-triggering us).
    if (this.routesReq && prevProps.tenantRoutesRes !== this.props.tenantRoutesRes) {
      this.routesReq = false;
      this.applyRoutesResponse(this.props.tenantRoutesRes);
    }
    if (this.opsReq && prevProps.operatorsList !== this.props.operatorsList) {
      this.opsReq = false;
      const res = this.props.operatorsList;
      if (res?.success) this.setState({operators: res.data?.operators || []});
    }
    if (this.setReq && prevProps.setRouteRes !== this.props.setRouteRes) {
      this.setReq = false;
      this.handleSetSettled(this.props.setRouteRes);
    }
    if (this.removeReq && prevProps.removeRouteRes !== this.props.removeRouteRes) {
      this.removeReq = false;
      this.handleRemoveSettled(this.props.removeRouteRes);
    }
  }

  fetchAll = () => {
    const {tenantId} = this.props;
    if (!tenantId) return;
    this.opsReq = true;
    this.props.listOperators();
    this.fetchRoutes();
  };

  fetchRoutes = () => {
    const {tenantId} = this.props;
    if (!tenantId) return;
    this.routesReq = true;
    this.setState({loading: true});
    // Search by org id → the single matching tenant row (with its nested
    // `routes` array). page_size 1 is enough; the backend matches on org id.
    this.props.listTenantRoutes(tenantId, 1, 5);
  };

  applyRoutesResponse = (res) => {
    if (!res?.success) {
      this.setState({loading: false, snackbar: {open: true,
        message: res?.message || res?.error || 'Failed to load routes', severity: 'error'}});
      return;
    }
    const d = res.data || res;
    const tenants = d.tenants || [];
    const me = tenants.find(t =>
      (t.tenant_id || t.tenantId || t.org_id || t.orgId) === this.props.tenantId) || tenants[0];
    const raw = (me && Array.isArray(me.routes)) ? me.routes : [];
    this.setState({routes: raw.map(normRoute), loading: false});
  };

  // ── Assign / reassign ────────────────────────────────────────────────────
  openAssign = (messageType, editRoute = null) => {
    // Default priority: reuse the edited route's, else next-after-max for the
    // type (so a 2nd operator lands as a standby, not colliding on primary).
    const forType = this.state.routes.filter(r => r.messageType === messageType);
    const defaultPriority = editRoute
      ? editRoute.priority
      : (forType.length ? Math.max(...forType.map(r => r.priority || 0)) + 1 : 1);
    this.setState({assignDialog: {
      messageType,
      operatorId: editRoute ? '' : '',
      priority: defaultPriority,
      editRouteId: editRoute ? editRoute.routeId : null,
    }});
  };

  closeAssign = () => this.setState({assignDialog: null});

  updateAssign = (field, value) =>
    this.setState(s => ({assignDialog: {...s.assignDialog, [field]: value}}));

  submitAssign = () => {
    const {assignDialog} = this.state;
    if (!assignDialog || !assignDialog.operatorId) return;
    // Reassign: set the new operator first, then delete the old route once the
    // new one is confirmed (handleSetSettled chains the remove).
    this.pendingRemoveRouteId = assignDialog.editRouteId || null;
    this.setReq = true;
    this.setState({busy: true});
    this.props.setOperatorRoute({
      tenantId: this.props.tenantId,
      operatorId: assignDialog.operatorId,
      productCode: 'sms',
      messageType: assignDialog.messageType,
      priority: parseInt(assignDialog.priority, 10) || 1,
      weight: 100,
    });
  };

  handleSetSettled = (res) => {
    if (!res?.success) {
      this.pendingRemoveRouteId = null;
      this.setState({busy: false, snackbar: {open: true,
        message: res?.message || res?.error || 'Failed to set route', severity: 'error'}});
      return;
    }
    if (this.pendingRemoveRouteId) {
      // Reassign leg 2: new operator is in place → retire the old route.
      const rid = this.pendingRemoveRouteId;
      this.pendingRemoveRouteId = null;
      this.removeReq = true;
      this.props.removeOperatorRoute(rid);
      return;
    }
    this.setState({busy: false, assignDialog: null,
      snackbar: {open: true, message: 'Route assigned', severity: 'success'}});
    this.fetchRoutes();
  };

  // ── Remove ───────────────────────────────────────────────────────────────
  removeRoute = (route) => {
    if (!window.confirm(`Remove the ${route.messageType.toUpperCase()} route to ${route.smscId} (priority ${route.priority})?`)) return;
    this.removeReq = true;
    this.setState({busy: true});
    this.props.removeOperatorRoute(route.routeId);
  };

  handleRemoveSettled = (res) => {
    if (!res?.success) {
      this.setState({busy: false, snackbar: {open: true,
        message: res?.message || res?.error || 'Failed to remove route', severity: 'error'}});
      return;
    }
    this.setState({busy: false, assignDialog: null,
      snackbar: {open: true, message: 'Route updated', severity: 'success'}});
    this.fetchRoutes();
  };

  activeOperators() {
    return this.state.operators.filter(op => op.is_active ?? op.isActive ?? true);
  }

  renderTypeSection(mt) {
    const {routes, busy} = this.state;
    const forType = routes
      .filter(r => r.messageType === mt.key)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
    return (
      <Box key={mt.key} sx={{py: 1.5, borderBottom: '1px solid #eee'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
          <Chip label={mt.label} size="small" color="default"
            sx={{fontWeight: 600, minWidth: 110}} />
          {forType.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No tenant route — uses global default
            </Typography>
          ) : forType.map(r => (
            <Box key={r.routeId} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Chip
                label={`${r.smscId}  ·  p=${r.priority}${r.isPrimary ? ' • primary' : ' • standby'}`}
                size="small"
                color={r.isPrimary ? 'primary' : 'default'}
                variant={r.isPrimary ? 'filled' : 'outlined'}
                onDelete={busy ? undefined : () => this.removeRoute(r)}
                deleteIcon={<Tooltip title="Remove this route"><Close /></Tooltip>}
              />
              <Tooltip title={`Reassign ${mt.label} to a different operator (keeps priority ${r.priority})`}>
                <span>
                  <IconButton size="small" disabled={busy}
                    onClick={() => this.openAssign(mt.key, r)}>
                    <SwapHoriz fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
          <Tooltip title={`Add a ${mt.label} route (primary if none, else a standby failover)`}>
            <span>
              <IconButton size="small" color="primary" disabled={busy}
                onClick={() => this.openAssign(mt.key)}>
                <Add fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  render() {
    const {tenantId} = this.props;
    const {loading, assignDialog, busy, snackbar, routes, subTab} = this.state;
    const activeOps = this.activeOperators();
    const totalRoutes = routes.length;

    return (
      <Box>
        <Tabs
          value={subTab}
          onChange={(e, v) => this.setState({subTab: v})}
          sx={{mb: 2, borderBottom: 1, borderColor: 'divider'}}
        >
          <Tab label="Operator Routing" />
          <Tab label="DLT TMD" />
        </Tabs>

        {subTab === 1 ? <DltTmdTab tenantId={tenantId} /> : this.renderRouting()}
      </Box>
    );
  }

  renderRouting() {
    const {tenantId} = this.props;
    const {loading, assignDialog, busy, snackbar, routes} = this.state;
    const activeOps = this.activeOperators();
    const totalRoutes = routes.length;

    return (
      <Box>
        <Card sx={{mb: 2}}>
          <CardContent>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
              <Box sx={{minWidth: 0}}>
                <Typography variant="h6">Operator Routing</Typography>
                <Typography variant="caption" color="text.secondary">
                  Per-message-type SMSC route for this tenant. A type with no route falls back to the global default. Priority 1 = primary; higher numbers are standby failovers.
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5}}>
                  Tenant: <code>{tenantId || '-'}</code> &nbsp;·&nbsp; {totalRoutes} route{totalRoutes === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={this.fetchRoutes} disabled={loading || busy}>
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress /></Box>
            ) : !tenantId ? (
              <Box sx={{textAlign: 'center', py: 4, color: 'text.secondary'}}>
                <RouteIcon sx={{fontSize: 48, color: '#ccc'}} />
                <Typography variant="body2" sx={{mt: 1}}>No tenant selected.</Typography>
              </Box>
            ) : (
              <Box>
                {MESSAGE_TYPES.map(mt => this.renderTypeSection(mt))}
                {activeOps.length === 0 && (
                  <Alert severity="info" sx={{mt: 2}}>
                    No SMSC operators are configured yet. Add operators in
                    <strong> Manage Operators → SMSC Operators</strong> before assigning routes.
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Assign / reassign dialog */}
        <Dialog open={!!assignDialog} onClose={this.closeAssign} maxWidth="sm" fullWidth>
          <DialogTitle>
            {assignDialog?.editRouteId ? 'Reassign' : 'Add'} route
            {assignDialog ? ` — ${(MESSAGE_TYPES.find(m => m.key === assignDialog.messageType) || {}).label}` : ''}
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{mt: 1, mb: 2}}>
              <InputLabel>SMSC Operator</InputLabel>
              <Select
                value={assignDialog?.operatorId || ''}
                label="SMSC Operator"
                onChange={(e) => this.updateAssign('operatorId', e.target.value)}
              >
                {activeOps.map(op => {
                  // value MUST be the operator's UUID `id` — the backend's
                  // operator_id is the UUID, NOT the smscId/code (which is
                  // only shown for humans). Sending the smscId 500s.
                  const sid = op.smsc_id || op.smscId;
                  return <MenuItem key={op.id} value={op.id}>{op.name} ({sid})</MenuItem>;
                })}
              </Select>
            </FormControl>
            <TextField
              label="Priority"
              type="number"
              fullWidth
              value={assignDialog?.priority ?? 1}
              onChange={(e) => this.updateAssign('priority', e.target.value)}
              helperText="1 = primary; higher numbers are standby failovers used in order."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeAssign} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={this.submitAssign}
              disabled={busy || !assignDialog?.operatorId}>
              {busy ? <CircularProgress size={20} /> : (assignDialog?.editRouteId ? 'Reassign' : 'Assign')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => this.setState({snackbar: {...snackbar, open: false}})}
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        >
          <Alert severity={snackbar.severity} onClose={() => this.setState({snackbar: {...snackbar, open: false}})}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

AdminRoutingTab.propTypes = {
  tenantId: PropTypes.string,
  operatorsList: PropTypes.object,
  tenantRoutesRes: PropTypes.object,
  setRouteRes: PropTypes.object,
  removeRouteRes: PropTypes.object,
  listOperators: PropTypes.func.isRequired,
  listTenantRoutes: PropTypes.func.isRequired,
  setOperatorRoute: PropTypes.func.isRequired,
  removeOperatorRoute: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    operatorsList: state.manageOperators.listOperatorsResponse,
    tenantRoutesRes: state.manageOperators.listTenantRoutesResponse,
    setRouteRes: state.manageOperators.setRouteResponse,
    removeRouteRes: state.manageOperators.removeRouteResponse,
  };
}

export default connect(mapStateToProps, {
  listOperators,
  listTenantRoutes,
  setOperatorRoute,
  removeOperatorRoute,
})(AdminRoutingTab);
